<template>
  <div class="crm-aniversariantes-page">
    <div class="page-header">
      <div class="header-top">
        <div>
          <h2 class="page-title">CRM - Aniversariantes do Mês</h2>
          <p class="page-subtitle">Contatos que fazem aniversário neste mês</p>
        </div>
        <div class="header-actions">
          <b-form-select v-model="mesSelecionado" size="sm" class="month-select" @change="loadAniversariantes">
            <b-form-select-option v-for="m in meses" :key="m.value" :value="m.value">{{ m.text }}</b-form-select-option>
          </b-form-select>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" />
      <p class="mt-1 text-muted">Carregando aniversariantes...</p>
    </div>

    <b-card v-else no-body class="table-card">
      <b-table
        :items="aniversariantes"
        :fields="fields"
        responsive
        hover
        show-empty
        empty-text="Nenhum aniversariante encontrado neste mês"
        class="mb-0 crm-table"
      >
        <template #cell(dia)="{ item }">
          <span class="day-badge" :class="isToday(item.dia) ? 'day-today' : ''">
            {{ String(item.dia).padStart(2, '0') }}
          </span>
        </template>

        <template #cell(nome)="{ item }">
          <div class="contact-info">
            <span class="contact-name">{{ item.nome }}</span>
            <small v-if="item.cargo" class="contact-role">{{ item.cargo }}</small>
          </div>
        </template>

        <template #cell(cliente_razao)="{ item }">
          <span
            v-if="item.clienteid"
            class="client-link"
            @click="$router.push({ name: 'crm-cliente-detail', params: { id: item.clienteid } })"
          >
            {{ item.cliente_razao }}
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(telefone)="{ item }">
          {{ item.telefone || item.celular || '-' }}
        </template>

        <template #cell(actions)="{ item }">
          <div class="action-buttons">
            <button
              v-if="item.email"
              :class="['action-btn', isCardSent(item) ? 'btn-sent' : 'btn-send-card']"
              :disabled="isCardSent(item)"
              @click.stop="openCardPreview(item)"
              :title="isCardSent(item) ? 'Cartão Enviado' : 'Enviar Cartão Premium'"
            >
              <feather-icon v-if="isCardSent(item)" icon="CheckIcon" size="14" />
              <feather-icon v-else icon="GiftIcon" size="14" />
            </button>
            <b-button
              v-if="item.telefone || item.celular"
              variant="flat-success"
              class="action-btn-legacy"
              size="sm"
              :href="'tel:' + (item.telefone || item.celular)"
              title="Ligar"
            >
              <feather-icon icon="PhoneIcon" size="14" />
            </b-button>
            <b-button
              v-if="item.clienteid"
              variant="flat-primary"
              class="action-btn-legacy"
              size="sm"
              @click="$router.push({ name: 'crm-cliente-detail', params: { id: item.clienteid } })"
              title="Ver cliente"
            >
              <feather-icon icon="ExternalLinkIcon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>

      <div class="table-footer" v-if="aniversariantes.length > 0">
        <span class="table-info">{{ tableInfo }}</span>
      </div>
    </b-card>

    <cartao-aniversario
      :visible="showCardModal"
      :contact="selectedContact"
      @update:visible="showCardModal = $event"
      @sent="onCardSent"
    />
  </div>
</template>

<script>
import crmClient from '@/api/CRM/crm.client';
import {
  BCard, BTable, BFormSelect, BFormSelectOption,
  BButton, BSpinner,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import CartaoAniversario from '@/components/CartaoAniversario.vue';

export default {
  name: 'CrmAniversariantesPage',
  components: {
    BCard, BTable, BFormSelect, BFormSelectOption,
    BButton, BSpinner, CartaoAniversario
  },
  data() {
    const hoje = new Date();
    return {
      loading: false,
      showCardModal: false,
      selectedContact: null,
      sentRecords: {},
      mesSelecionado: hoje.getMonth() + 1,
      aniversariantes: [],
      diaHoje: hoje.getDate(),
      mesHoje: hoje.getMonth() + 1,
      meses: [
        { value: 1, text: 'Janeiro' },
        { value: 2, text: 'Fevereiro' },
        { value: 3, text: 'Março' },
        { value: 4, text: 'Abril' },
        { value: 5, text: 'Maio' },
        { value: 6, text: 'Junho' },
        { value: 7, text: 'Julho' },
        { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setembro' },
        { value: 10, text: 'Outubro' },
        { value: 11, text: 'Novembro' },
        { value: 12, text: 'Dezembro' },
      ],
      fields: [
        { key: 'dia', label: 'Dia', class: 'text-center', thStyle: { width: '70px' } },
        { key: 'nome', label: 'Contato' },
        { key: 'cliente_razao', label: 'Cliente' },
        { key: 'telefone', label: 'Telefone' },
        { key: 'actions', label: 'Ações', class: 'text-center', thStyle: { width: '100px' } },
      ],
    };
  },
  computed: {
    tableInfo() {
      const count = this.aniversariantes.length;
      const plural = count === 1 ? '' : 's';
      return `${count} aniversariante${plural} encontrado${plural}`;
    },
  },
  created() {
    this.loadAniversariantes();
  },
  methods: {
    async loadAniversariantes() {
      this.loading = true;
      try {
        const result = await crmClient.aniversariosMes(this.mesSelecionado);
        const payload = result.data || result;
        this.aniversariantes = Array.isArray(payload) ? payload : (payload.items || payload.rows || []);
        this.cacheSentStatus();
      } catch (error) {
        console.error('Erro ao carregar aniversariantes:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar aniversariantes',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    isToday(dia) {
      return this.mesSelecionado === this.mesHoje && Number(dia) === this.diaHoje;
    },
    cacheSentStatus() {
      const year = new Date().getFullYear();
      const records = {};
      this.aniversariantes.forEach(item => {
        const key = `birthday_sent_${item.clienteiderp || item.email}_${year}`;
        if (localStorage.getItem(key)) {
          records[key] = true;
        }
      });
      this.sentRecords = { ...records };
    },
    isCardSent(item) {
      const year = new Date().getFullYear();
      const key = `birthday_sent_${item.clienteiderp || item.email}_${year}`;
      return !!this.sentRecords[key];
    },
    openCardPreview(contact) {
      this.selectedContact = contact;
      this.showCardModal = true;
    },
    onCardSent(contact) {
      const year = new Date().getFullYear();
      const key = `birthday_sent_${contact.clienteiderp || contact.email}_${year}`;
      this.$set(this.sentRecords, key, true);
    },
  },
};
</script>

<style scoped>
.crm-aniversariantes-page {
  padding: 0.75rem;
}
.page-header {
  margin-bottom: 1.25rem;
}
.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}
.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.month-select {
  min-width: 160px;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-heading, #5e5873);
  margin-bottom: 0.25rem;
}
.page-subtitle {
  color: var(--text-muted, #b9b9c3);
  font-size: 0.95rem;
  margin-bottom: 0;
}
.table-card {
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(34,41,47,0.06);
  background: var(--card-bg, #fff);
  border-color: var(--border-color, #ebe9f1);
  overflow: hidden;
}
/* Use bootstrap-vue defaults (theme-aware) — no custom overrides */
.day-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  background: var(--subtle-bg, #f3f2f7);
  color: var(--text-heading, #5e5873);
}
.day-today {
  background: #16a34a;
  color: #fff;
}
.contact-info {
  display: flex;
  flex-direction: column;
}
.contact-name {
  font-weight: 600;
  color: var(--text-heading, #5e5873);
}
.contact-role {
  color: var(--text-muted, #b9b9c3);
  font-size: 0.78rem;
}
.client-link {
  color: var(--primary, #2563eb);
  cursor: pointer;
  font-weight: 500;
}
.client-link:hover {
  text-decoration: underline;
}
.action-buttons {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}
.table-footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--border-color, #ebe9f1);
}
.table-info {
  font-size: 0.85rem;
  color: var(--text-muted, #b9b9c3);
  background: transparent;
  box-shadow: none;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-color, #e2e8f0);
  background: var(--card-bg, #fff);
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}
.action-btn-legacy {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-send-card {
  color: #c87941;
  border-color: #c87941;
  background: rgba(200, 121, 65, 0.05);
}
.btn-send-card:hover:not(:disabled) {
  background: linear-gradient(135deg, #c87941, #f2c88a);
  color: #040b15;
  border-color: transparent;
  box-shadow: 0 4px 10px rgba(200, 121, 65, 0.2);
}

.btn-sent {
  background: #f1f5f9;
  color: #10b981;
  border-color: #10b981;
  cursor: not-allowed;
  opacity: 0.8;
}

.dark-layout .crm-aniversariantes-page {
  --card-bg: #1f2430;
  --border-color: #2b3242;
  --subtle-bg: #262c3a;
  --text-heading: #e5e7eb;
  --text-body: #c7cbd6;
  --text-muted: #8c93a8;
}

.dark-layout .day-badge {
  background: var(--subtle-bg, #262c3a);
  color: var(--text-heading, #e5e7eb);
}

@media (max-width: 768px) {
  .header-top {
    flex-direction: column;
  }
}
</style>
